'use client';

import { useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { LogoIcon } from '@/_components/LogoIcon';

const details = ({ className, data, char, onClick }) => {

    let text = data;
    if (text && Array.isArray(text)) {
        let filteredArray = text.filter((value) => value && value.trim() !== '');
        text = filteredArray?.join('');
    }
    let str = text || '';
    if (text) {
        str = text?.substring(0, char);
        if (text?.length > char) {
            str = str + '...';
        }
    }
    return (
        <span className={className} onClick={onClick} style={{ minHeight: '50px', display: 'block' }}>
            <span dangerouslySetInnerHTML={{ __html: str }} className="text-primary" />
        </span>
    )
}


export const WarrantyDetails = ({ data, allowCOD = true, replacementAllow = 7, product }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { duration, info, time } = data || {};
    const hasWarranty = duration && Number(duration) > 0;
    const coverImage = product?.coverImage;
    const productName = product?.title;
    const avgRating = product?.averageRating || 0;

    // ── All available cards ──────────────────────────────────────────
    const allCards = [
        {
            key: 'replacement',
            icon: <i className="pi pi-cog text-base" />,
            label: `${replacementAllow} Days Replacement`,
            desc: `We offer a ${replacementAllow}-day replacement guarantee from the date of delivery. If you receive a product that is damaged, defective, or not functioning properly, you are eligible to request a replacement within ${replacementAllow} days.`,
        },
        {
            key: 'brand',
            icon: <LogoIcon size={18} />,
            label: 'Brand Assured',
            desc: 'At Ennix Electronics, we prioritize quality, reliability, and customer satisfaction. All our products are carefully tested to meet high standards of performance and durability.',
        },
        {
            key: 'warranty',
            icon: <i className={`pi ${hasWarranty ? 'pi-shield' : 'pi-times-circle'} text-base`} />,
            label: hasWarranty ? `${duration} ${time} Warranty` : 'No Warranty',
            desc: hasWarranty
                ? (info || `This product includes a ${duration} ${time} manufacturer warranty.`)
                : "We don't offer warranty for this product.",
        },
        {
            key: 'transit',
            icon: <i className="pi pi-truck text-base" />,
            label: 'Transit Safe',
            desc: 'If your product is damaged during transit, we will replace it at no extra cost. Please report any transit damage within 48 hours of delivery with supporting photos.',
        },
        ...(allowCOD ? [{
            key: 'cod',
            icon: <i className="pi pi-indian-rupee text-base" />,
            label: 'Cash on Delivery',
            desc: 'Available. Select Cash on Delivery (CoD) while placing the order and pay in cash at the time of delivery. No advance payment needed.',
        }] : []),
    ];

    const VISIBLE = 3;
    const visibleCards = allCards.slice(0, VISIBLE);
    const hiddenCount = allCards.length - VISIBLE;

    return (
        <>
            {/* ── compact cards + "show more" button ────── */}
            <div style={{
                display: 'flex', gap: '4px', alignItems: 'stretch',
                marginTop: '6px', marginBottom: '6px',
                background: 'rgba(11,49,99,0.02)', padding: '8px 6px',
                borderRadius: '8px', border: '1px solid rgba(11,49,99,0.1)',
            }}>
                {visibleCards.map((card) => (
                    <div
                        key={card.key}
                        onClick={() => setSidebarOpen(true)}
                        style={{
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                            gap: '4px', flex: 1, minWidth: 0, cursor: 'pointer',
                        }}
                    >
                        <span style={{
                            width: 28, height: 28,
                            background: 'rgba(11,49,99,0.07)',
                            border: '1px solid rgba(11,49,99,0.13)',
                            borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'var(--primary-color)',
                            fontSize: '0.75rem',
                        }}>
                            {card.icon}
                        </span>
                        <small style={{ fontSize: '0.58rem', textAlign: 'center', lineHeight: 1.25, color: 'var(--primary-color)', fontWeight: 600 }}>
                            {card.label}
                        </small>
                    </div>
                ))}

                {hiddenCount > 0 && (
                    <div style={{ width: '1px', background: 'rgba(11,49,99,0.08)', alignSelf: 'stretch', margin: '0 2px' }} />
                )}

                {hiddenCount > 0 && (
                    <div
                        onClick={() => setSidebarOpen(true)}
                        style={{
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                            gap: '4px', minWidth: 40, cursor: 'pointer',
                        }}
                    >
                        <span style={{
                            width: 28, height: 28,
                            background: 'rgba(27,184,167,0.08)',
                            border: '1px solid rgba(27,184,167,0.2)',
                            borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'var(--secondary-color)',
                            fontSize: '0.75rem',
                        }}>
                            <i className="pi pi-angle-right" />
                        </span>
                        <small style={{ fontSize: '0.58rem', textAlign: 'center', lineHeight: 1.25, color: 'var(--secondary-color)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            +{hiddenCount} more
                        </small>
                    </div>
                )}
            </div>

            {/* ── Warranty info line ────────────────────────── */}
                {!hasWarranty &&  (
                    <div className="flex align-items-center gap-2 mb-2" style={{
                        padding: '0.45rem 0.75rem',
                        borderRadius: '8px',
                        background: 'rgba(234,88,12,0.07)',
                        border: '1px solid rgba(234,88,12,0.25)',
                    }}>
                        <i className="pi pi-exclamation-triangle"
                            style={{ flexShrink: 0, color: '#ea580c', fontSize: '0.78rem' }} />
                        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#c2410c' }}>
                            We don&apos;t offer warranty for this product.
                        </span>
                    </div>
                )}
         

            {/* ── Replacement notice — only when no warranty ── */}
            {Boolean(hasWarranty) && (
                <div className="flex align-items-center gap-2 mb-2" style={{
                    padding: '0.6rem 0.9rem',
                    borderRadius: '8px',
                    background: 'linear-gradient(90deg, rgba(22,163,74,0.12) 0%, rgba(22,163,74,0.04) 100%)',
                    border: '1.5px solid rgba(22,163,74,0.4)',
                    borderLeft: '4px solid #16a34a',
                    boxShadow: '0 2px 8px rgba(22,163,74,0.1)',
                }}>
                    <i className="pi pi-sync" style={{ flexShrink: 0, color: '#16a34a', fontSize: '0.85rem' }} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#15803d', lineHeight: 1.4 }}>
                        We offer a replacement if the product is not working.{' '}
                        <a href="/support" style={{ color: '#0b3163', textDecoration: 'underline', fontWeight: 700 }}>
                            For more details, please visit our support center.
                        </a>
                    </span>
                </div>
            )}

            {/* ── Right Sidebar ─────────────────────────────── */}
            <Sidebar
                visible={sidebarOpen}
                position="right"
                onHide={() => setSidebarOpen(false)}
                style={{ width: 'min(90vw, 38rem)' }}
                header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                            width: '28px', height: '28px', borderRadius: '8px',
                            background: 'var(--primary-color)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <i className="pi pi-verified" style={{ fontSize: '0.75rem', color: '#fff' }} />
                        </div>
                        <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#1e293b' }}>Purchase Benefits</span>
                    </div>
                }
                pt={{
                    header: { style: { background: 'var(--surface-100, #f8fafc)', borderBottom: '1px solid var(--surface-200, #e2e8f0)', padding: '12px 16px' } },
                    content: { style: { padding: 0 } },
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>

                    {/* Product hero */}
                    {product && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '14px',
                            padding: '14px 16px',
                            background: 'linear-gradient(135deg, #f0fdfa 0%, #fff 100%)',
                            borderBottom: '1px solid #e2e8f0',
                        }}>
                            {coverImage ? (
                                <div style={{
                                    width: '60px', height: '60px', borderRadius: '12px',
                                    background: '#fff', border: '1.5px solid rgba(11,49,99,0.2)',
                                    flexShrink: 0, overflow: 'hidden', padding: '4px',
                                    boxShadow: '0 4px 12px rgba(11,49,99,0.1)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <img src={coverImage} alt={productName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                </div>
                            ) : (
                                <div style={{
                                    width: '60px', height: '60px', borderRadius: '12px',
                                    background: 'var(--primary-color)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                    boxShadow: '0 4px 12px rgba(11,49,99,0.22)',
                                }}>
                                    <i className="pi pi-box" style={{ color: '#fff', fontSize: '1.4rem' }} />
                                </div>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{
                                    margin: '0 0 6px', fontWeight: 700, fontSize: '0.85rem',
                                    color: 'var(--primary-color)',
                                    display: '-webkit-box', WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                }}>
                                    {productName}
                                </p>
                                {avgRating > 0 && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        {[1,2,3,4,5].map(s => (
                                            <i key={s}
                                                className={`pi ${s <= Math.round(avgRating) ? 'pi-star-fill' : 'pi-star'}`}
                                                style={{ fontSize: '0.6rem', color: s <= Math.round(avgRating) ? '#f59e0b' : '#d1d5db' }}
                                            />
                                        ))}
                                        <span style={{ fontSize: '0.68rem', color: 'var(--secondary-color)', fontWeight: 700, marginLeft: '4px' }}>
                                            {Number(avgRating).toFixed(1)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Cards */}
                    <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {allCards.map((card) => (
                            <div
                                key={card.key}
                                style={{
                                    display: 'flex', alignItems: 'flex-start', gap: '12px',
                                    padding: '14px', borderRadius: '10px',
                                    border: '1px solid rgba(11,49,99,0.12)',
                                    background: '#fff',
                                }}
                            >
                                <div style={{
                                    width: 40, height: 40, minWidth: 40, borderRadius: '10px',
                                    background: 'rgba(11,49,99,0.06)',
                                    border: '1px solid rgba(11,49,99,0.12)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: 'var(--primary-color)',
                                }}>
                                    {card.icon}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--primary-color)' }}>{card.label}</span>
                                    <span style={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.5 }}>{card.desc}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Sidebar>
        </>
    );
}
export const Template = {
    details,
    WarrantyDetails,
}